import tenantTheme from '@theme';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, TextInput, TextWithIcon, notification, Switch, Text } from '../../../../components/common';
import { generateDescriptionClickEvent, generateTitleClickEvent } from '../../../../services/analyticsService';
import { GenerateTitle } from './styled';
import { capitalizeFirstLetter } from '../../../../utility/utility';
import tenantConstants from '@constants';
import { useGetAutoGeneratedListingContentMutation } from '../../../../apis/postlisting';
import { useLazyGetAutoGeneratedUserContentQuery } from '../../../../apis/user';
const GenerateContentField = (props) => {
  const {
    formik,
    dependsOn,
    setSubmitDisable = () => {},
    lineCount,
    limit,
    name,
    label,
    labelIcon,
    placeholder,
    dir,
    payloadKey,
    desc,
    skipField,
    disabled,
    className,
    entityType = 'listing',
    btnText,
    showAutoSwitch = false,
    reserveLabelIconColumn = false,
    followUiDir = false,
    contentDir,
  } = props;

  const isRtl = dir === 'rtl';
  const uiLocale = useSelector((state) => state.app.AppConfig.locale);
  const isUiArabic = uiLocale === 'ar';
  const localeAwareGenerateContentFields = tenantConstants?.LOCALE_AWARE_GENERATE_CONTENT_FIELDS === true;
  const isEnglishFieldInArabicUi = localeAwareGenerateContentFields && isUiArabic && !isRtl;
  const uiDir = isUiArabic ? 'rtl' : 'ltr';
  const fieldDir = contentDir || uiDir;

  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const [isChanged, setIsChanged] = useState(null);
  const { [name]: field, [skipField]: fieldToSkip, ...rest } = formik.values;
  const { user } = useSelector((state) => state.app.loginUser);


  const [getAutoGeneratedContentListing, { isLoading: isLoadingListing }] =
    useGetAutoGeneratedListingContentMutation();
  const [getAutoGeneratedContentUser, { isLoading: isLoadingUser, isFetching: isFetchingUser }] =
    useLazyGetAutoGeneratedUserContentQuery();
  
  // Select the appropriate hook based on entityType
  const getAutoGeneratedContent = entityType === 'listing' ? getAutoGeneratedContentListing : getAutoGeneratedContentUser;
  // For mutations, isLoading is the loading state. For queries, use isFetching (or isLoading)
  const isLoading = entityType === 'listing' ? isLoadingListing : isLoadingUser;
  const isFetching = entityType === 'listing' ? isLoadingListing : (isFetchingUser || isLoadingUser);

  const entityConfig = {
    user: {
      extraParams: (values, user) => ({ userId: user?.id }),
      reviewMessage: `Please review the ${desc} after editing other fields`,
      isMissingFields: (values) => !values?.serviceArea?.length || !values?.languages?.length || !values?.experience,
      getTrackedValues: (values, dependsOn) =>
        dependsOn?.reduce((acc, key) => {
          acc[key] = values[key];
          return acc;
        }, {}),
    },
    listing: {
      extraParams: (values, user) => ({ listingId: values?.id }),
      reviewMessage: `Please review the ${desc} after editing the listing`,
      isMissingFields: () => false,
      getTrackedValues: (values) => {
        const { [name]: field, [skipField]: fieldToSkip, ...rest } = values;
        return rest || {};
      },
    },
  };

  useEffect(() => {
    if (!isChanged) return;
    setIsChanged(true);
  }, [JSON.stringify(entityConfig[entityType].getTrackedValues(formik.values, dependsOn))]);

  useEffect(() => {
    isChanged != null && setIsChanged(false);
  }, [field]);

  function getGenerationInputs(formikValues, dependsOn) {
    const keys = dependsOn ?? Object.keys(formikValues);
    return keys.reduce((acc, key) => {
      acc[key] = formikValues[key];
      return acc;
    }, {});
  }

  const fetchAutoGeneratedContent = async () => {
    setSubmitDisable(true);

    const listingType = formik?.values?.listing_type?.id ? {
        id: formik?.values?.listing_type?.id,
        title: formik?.values?.listing_type?.title,
        title_l1: formik?.values?.listing_type?.title_l1
    } : null;

    const formikValues = {
      ...formik?.values,
      listing_type: listingType
    }

    const response = await getAutoGeneratedContent({
      values: getGenerationInputs(formikValues, dependsOn),
      content_type: payloadKey,
      listingId: formikValues?.id,
      ...entityConfig[entityType]?.extraParams(formikValues, user),
    });

    if (response) {
      desc === 'title'
        ? generateTitleClickEvent(user, response, formikValues)
        : generateDescriptionClickEvent(user, response, formikValues);
      setSubmitDisable(false);
      if (response?.error) {
        notification.error(response?.error);
      } else {
        // API: { content: string, success: boolean } — RTK mutation unwraps to response.data
        const payload = response?.data ?? response;
        const content = typeof payload?.content === 'string' ? payload.content : null;
        if (content != null) {
          formik.setFieldValue(name, content, true);
          setIsChanged(false);
        }
      }
    } else {
      setSubmitDisable(false);
    }
  };

  const renderReviewMessage = () => {
    return (
      isChanged && (
        <TextWithIcon
          gap="4px"
          className={isMobile ? 'fz-10' : 'fz-14'}
          align={isMobile && 'baseline'}
          textColor={tenantTheme['warning-color']}
          icon="RiErrorWarningFill"
          iconProps={{ color: tenantTheme['warning-color'], size: isMobile && '10px' }}
          title={t(entityConfig[entityType].reviewMessage)}
        />
      )
    );
  };

  const renderGenerateButton = () => {
    return (
      <GenerateTitle align="center" justify="space-between" gap="8px" style={{ width: '100%' }}>
        {showAutoSwitch && tenantConstants?.AUTO_TRANSLATE_CONTENT && (
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Switch
              switchOnly
              size="small"
              name={`generate_${desc}`}
              value={formik?.values[`generate_${desc}`]}
              onChange={(checked) => {
                formik?.setFieldValue(`generate_${desc}`, checked);
              }}
              onBlur={() => formik?.setFieldTouched(`generate_${desc}`, true)}
            />
            <Text style={{ marginInlineStart: 6, fontSize: isMobile ? 10 : 13, lineHeight: '1.1' }}>
              {t(`Auto Translate ${isUiArabic ? 'English' : 'Arabic'} ${capitalizeFirstLetter(desc)}`)}
            </Text>
          </div>
        )}
        <Button
          // className="generate-btn"
          shape="round"
          icon="BsStars"
          iconProps={{ size: isMobile ? '10px' : '12px' }}
          type="primaryOutlined"
          size="small"
          style={{
            fontSize: isMobile && '10px',
            border: 0,
            marginInlineStart: 'auto',
          }}
          disabled={isLoading || disabled || entityConfig[entityType].isMissingFields(formik?.values)}
          loading={isFetching}
          onClick={fetchAutoGeneratedContent}
        >
          {btnText || t('Generate', isUiArabic ? { lng: 'ar' } : {}) + ` ${label}`}
        </Button>
      </GenerateTitle>
    );
  };

  const renderField = () => (
    <TextInput
      dir={
        followUiDir
          ? fieldDir
          : localeAwareGenerateContentFields
            ? (isRtl ? 'rtl' : 'ltr')
            : dir
      }
      lang={
        followUiDir
          ? fieldDir === 'rtl' ? 'ar' : 'en'
          : localeAwareGenerateContentFields
            ? (isRtl ? 'ar' : 'en')
            : isRtl ? 'ar' : undefined
      }
      key={name}
      name={name}
      label={label}
      labelIcon={labelIcon}
      reserveLabelIconColumn={reserveLabelIconColumn}
      reserveLabelIconColumnAtEnd={!followUiDir && isEnglishFieldInArabicUi && reserveLabelIconColumn}
      labelProps={
        followUiDir
          ? { dir: fieldDir, lang: fieldDir === 'rtl' ? 'ar' : 'en', style: { textAlign: fieldDir === 'rtl' ? 'right' : 'left', width: '100%' } }
          : isRtl
            ? { dir: 'rtl', lang: 'ar', style: { textAlign: 'right', width: '100%' } }
            : isEnglishFieldInArabicUi
              ? { dir: 'ltr', lang: 'en', style: { textAlign: 'left', width: '100%' } }
              : undefined
      }
      groupStyle={
        followUiDir
          ? { direction: fieldDir }
          : isEnglishFieldInArabicUi
            ? { direction: 'ltr', unicodeBidi: 'isolate' }
            : undefined
      }
      placeholder={placeholder}
      useInternalState
      handleChange={formik?.handleChange}
      handleBlur={(event) => {
        formik.setFieldTouched(name, true);
        formik.setFieldValue(name, event.target.value, true);
      }}
      value={formik?.values[name]}
      disabled={isFetching}
      errorMsg={formik?.errors[name] && formik?.touched[name] && formik?.errors[name]}
      style={{ borderColor: isChanged && tenantTheme['warning-color'] }}
      lineCount={lineCount}
      limit={limit}
      extra={!formik?.errors[name] && renderReviewMessage}
      button={renderGenerateButton}
    />
  );

  return (
    <div
      className={className || undefined}
      style={{
        width: '100%',
        ...(followUiDir
          ? { direction: fieldDir }
          : isEnglishFieldInArabicUi
            ? { direction: 'ltr', unicodeBidi: 'isolate' }
            : {}),
      }}
    >
      {renderField()}
    </div>
  );
};

export default GenerateContentField;
